import { parseConceptCues, parseConceptPresentation, parseTraceReference, parseTraceScenePlan } from '@/_pure/episode';
import { normalizeAuthoredBeat, parseNarrationPauses, parseNarrationTextMap } from '@/_pure/episodeAuthoring';
import { parseEpisodeTraceCameraPolicy } from '@/_pure/episodeCamera';
import { parseEpisodeExcerpt } from '@/_pure/episodeExcerpt';
import { parseEpisodeComments } from '@/_pure/episodeComments';
import { parseEpisodeVisualSources, resolveEpisodeVisualSource } from '@/_pure/episodeVisualSource';
import { withQuotientGrowthIllustration } from './derivation/natural/quotientGrowthIllustration';
import { configForPrimitiveTrace, projectPrimitiveTracePayload } from '@/adapters/primitive/_pure/derivation/primitiveSteps';
import { parsePrimitiveSceneDirection } from '@/adapters/primitive/_pure/derivation/sceneDirection';
import { parsePrimitivePlanEffects } from './planPresentation';
import type { Config, RuntimeState } from '@/adapters/primitive/_contract/config';
import type { NaturalBandLabels, PrimitiveConceptArrangement, PrimitiveConceptBeat, PrimitiveConceptTarget, PrimitiveEpisode, PrimitiveScene } from '@/adapters/primitive/_contract/episode';
import type { PrimitiveVisualSource } from '@/adapters/primitive/_contract/visualSource';

export function primitiveConceptValue(arrangement: PrimitiveConceptArrangement): number {
    if (arrangement.arrangementKind === 'natural-wheel') return arrangement.value;
    if (arrangement.arrangementKind === 'boolean-wheel') return Number(arrangement.value);
    if (arrangement.arrangementKind === 'primitive-overview') return arrangement.naturalValue;
    return 10 ** (arrangement.power + (arrangement.bandKind === 'unit' ? 0 : 1));
}

export function detachPrimitiveEpisode(state: RuntimeState, config: Config): RuntimeState {
    const exploration = state.conceptArrangement ? state.explorationBeforeConcept : state;
    if (!exploration) throw new Error('Concept scene lost its Exploration checkpoint.');
    let committedStepIndex = exploration.committedStepIndex ?? 0;
    if (!state.conceptArrangement && state.episode && state.selectedTrace) {
        const effective = configForPrimitiveTrace(config, state.selectedTrace);
        const episodeSteps = withQuotientGrowthIllustration(effective,
            projectPrimitiveTracePayload(effective, state.selectedTrace.payload, true, state.divisionComparison,
                undefined, state.fullComparisons),
            state.illustrativeQuotientBands, state.fullComparisons);
        const explorationSteps = projectPrimitiveTracePayload(effective, state.selectedTrace.payload);
        let index = committedStepIndex;
        // An episode-only approach has not yet completed the combined Exploration alignment action.
        while (index >= 0 && !explorationSteps.some(step => step.stepKey === episodeSteps[index]?.stepKey)) index--;
        if (index < 0) throw new Error('Episode lost its completed Exploration landmark.');
        committedStepIndex = explorationSteps.findIndex(step => step.stepKey === episodeSteps[index]?.stepKey);
    }
    return { ...exploration, committedStepIndex, pendingStepIndex: null, episode: undefined, sceneIndex: undefined,
        divisionComparison: undefined, fullComparisons: undefined, illustrativeQuotientBands: undefined, episodeVisualStepIndex: undefined,
        conceptArrangement: undefined, explorationBeforeConcept: undefined, sceneDirectionCatalogs: undefined,
        isPlaying: false, playbackAdvanceMode: 'manual' };
}

function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid Primitive episode object.');
    return value as Record<string, unknown>;
}

function text(value: unknown): string {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Primitive episode requires nonempty text.');
    return value;
}

function fields(value: Record<string, unknown>, allowed: readonly string[]) {
    for (const key of Object.keys(value)) {
        if (!allowed.includes(key)) throw new Error(`Unknown Primitive episode field '${key}'.`);
    }
}

function parseConceptArrangement(value: unknown): PrimitiveConceptArrangement {
    const arrangement = record(value);
    if (arrangement.arrangementKind === 'boolean-wheel') {
        fields(arrangement, ['arrangementKind', 'value']);
        if (typeof arrangement.value !== 'boolean') throw new Error('Invalid Boolean-wheel arrangement.');
        return { arrangementKind: 'boolean-wheel', value: arrangement.value };
    }
    if (arrangement.arrangementKind === 'primitive-overview') {
        fields(arrangement, ['arrangementKind', 'naturalValue', 'naturalPowers', 'booleanValue']);
        if (typeof arrangement.naturalValue !== 'number' || !Number.isInteger(arrangement.naturalValue)
            || arrangement.naturalValue < 0 || arrangement.naturalValue > 999
            || !Array.isArray(arrangement.naturalPowers) || !arrangement.naturalPowers.length
            || !arrangement.naturalPowers.every((power: unknown) => typeof power === 'number'
                && Number.isInteger(power) && power >= 0 && power <= 2)
            || new Set(arrangement.naturalPowers).size !== arrangement.naturalPowers.length
            || typeof arrangement.booleanValue !== 'boolean') throw new Error('Invalid Primitive overview arrangement.');
        return { arrangementKind: 'primitive-overview', naturalValue: arrangement.naturalValue,
            naturalPowers: arrangement.naturalPowers, booleanValue: arrangement.booleanValue };
    }
    if (arrangement.arrangementKind === 'natural-bands') {
        fields(arrangement, ['arrangementKind', 'bandKind', 'power', 'bandLabels']);
        if ((arrangement.bandKind !== 'unit' && arrangement.bandKind !== 'radix' && arrangement.bandKind !== 'equivalence')
            || typeof arrangement.power !== 'number' || !Number.isInteger(arrangement.power) || arrangement.power < 0
            || arrangement.power > (arrangement.bandKind === 'unit' ? 2 : 1)) throw new Error('Invalid Natural bands arrangement.');
        let bandLabels: Pick<NaturalBandLabels, 'radial' | 'angular'> | undefined;
        if (arrangement.bandLabels !== undefined) {
            const labels = record(arrangement.bandLabels);
            fields(labels, ['radial', 'angular']);
            if (typeof labels.radial !== 'boolean' || typeof labels.angular !== 'boolean') {
                throw new Error('Natural bands labels require radial and angular visibility booleans.');
            }
            bandLabels = { radial: labels.radial, angular: labels.angular };
        }
        return { arrangementKind: 'natural-bands', bandKind: arrangement.bandKind, power: arrangement.power,
            ...(bandLabels === undefined ? {} : { bandLabels }) };
    }
    fields(arrangement, ['arrangementKind', 'value', 'powers', 'focusedPowers', 'bandLabels']);
    if (arrangement.arrangementKind !== 'natural-wheel' || typeof arrangement.value !== 'number'
        || !Number.isInteger(arrangement.value) || arrangement.value < 0 || arrangement.value > 999
        || !Array.isArray(arrangement.powers) || !arrangement.powers.length
        || !arrangement.powers.every((power: unknown) => typeof power === 'number' && Number.isInteger(power) && power >= 0 && power <= 2)
        || new Set(arrangement.powers).size !== arrangement.powers.length) throw new Error('Invalid Natural-wheel arrangement.');
    const focusedPowers = arrangement.focusedPowers;
    const powers = arrangement.powers;
    if (focusedPowers !== undefined && (!Array.isArray(focusedPowers) || !focusedPowers.length
        || !focusedPowers.every((power: unknown) => typeof power === 'number' && powers.includes(power))
        || new Set(focusedPowers).size !== focusedPowers.length)) {
        throw new Error('Natural-wheel focused powers must be a nonempty, unique subset of its bands.');
    }
    let bandLabels: NaturalBandLabels | undefined;
    if (arrangement.bandLabels !== undefined) {
        const labels = record(arrangement.bandLabels);
        fields(labels, ['radial', 'angular', 'sweep']);
        if (typeof labels.radial !== 'boolean' || typeof labels.angular !== 'boolean') {
            throw new Error('Natural-wheel band labels require radial and angular visibility booleans.');
        }
        bandLabels = { radial: labels.radial, angular: labels.angular };
        if (labels.sweep !== undefined) {
            const sweep = record(labels.sweep);
            fields(sweep, ['afterCue', 'powers', 'durationMs']);
            if (!labels.radial || typeof sweep.afterCue !== 'string' || !/^[a-z][a-z0-9-]*$/.test(sweep.afterCue)
                || !Array.isArray(sweep.powers) || !sweep.powers.length
                || !sweep.powers.every((power: unknown) => typeof power === 'number' && powers.includes(power))
                || new Set(sweep.powers).size !== sweep.powers.length || typeof sweep.durationMs !== 'number'
                || !Number.isFinite(sweep.durationMs) || sweep.durationMs <= 0 || sweep.durationMs > 60000) {
                throw new Error('Band sweep requires visible radial labels, a cue, unique existing powers and a positive duration up to 60000 ms.');
            }
            bandLabels.sweep = { afterCue: sweep.afterCue, powers: sweep.powers, durationMs: sweep.durationMs };
        }
    }
    return { arrangementKind: 'natural-wheel', value: arrangement.value, powers: arrangement.powers,
        ...(bandLabels === undefined ? {} : { bandLabels }),
        ...(focusedPowers === undefined ? {} : { focusedPowers }) };
}

function parseConceptTarget(value: unknown): PrimitiveConceptTarget {
    const target = record(value);
    if (target.targetKind === 'boolean-disc') {
        fields(target, ['targetKind']);
        return { targetKind: 'boolean-disc' };
    }
    if (target.targetKind === 'natural-assembly-band') {
        fields(target, ['targetKind', 'bandKind', 'part']);
        if ((target.bandKind !== 'unit' && target.bandKind !== 'radix')
            || (target.part !== 'band' && target.part !== 'counted' && target.part !== 'zero')) {
            throw new Error('Concept Assembly targets require a unit or radix band and a band, counted or zero part.');
        }
        return { targetKind: 'natural-assembly-band', bandKind: target.bandKind, part: target.part };
    }
    if (target.targetKind === 'natural-angular-band') {
        fields(target, ['targetKind', 'digit']);
        if (typeof target.digit !== 'number' || !Number.isInteger(target.digit) || target.digit < 0 || target.digit > 9) {
            throw new Error('Concept angular bands require a digit from zero through nine.');
        }
        return { targetKind: 'natural-angular-band', digit: target.digit };
    }
    if (target.targetKind === 'natural-cells' || target.targetKind === 'natural-band') {
        fields(target, target.targetKind === 'natural-band' ? ['targetKind', 'power'] : ['targetKind', 'power', 'cells']);
        if (typeof target.power !== 'number' || !Number.isInteger(target.power)
            || target.power < 0 || target.power > 2) {
            throw new Error('Concept Natural targets require a band power from zero through two.');
        }
        if (target.targetKind === 'natural-band') return { targetKind: 'natural-band', power: target.power };
        if (target.cells !== 'counted' && target.cells !== 'all' && target.cells !== 'zero') {
            throw new Error('Concept cell targets select counted or all cells, or the zero position.');
        }
        return { targetKind: 'natural-cells', power: target.power, cells: target.cells };
    }
    fields(target, ['targetKind', 'valueKind']);
    if (target.targetKind !== 'primitive-wheel' || (target.valueKind !== 'natural' && target.valueKind !== 'boolean')) {
        throw new Error('Concept targets name a primitive wheel by its value kind.');
    }
    return { targetKind: 'primitive-wheel', valueKind: target.valueKind };
}

/** The stable Natural-wheel of a concept arrangement, if it has one. */
export function conceptNaturalWheel(arrangement: PrimitiveConceptArrangement): { value: number; powers: readonly number[] } | undefined {
    if (arrangement.arrangementKind === 'natural-wheel') return { value: arrangement.value, powers: arrangement.powers };
    if (arrangement.arrangementKind === 'primitive-overview') return { value: arrangement.naturalValue, powers: arrangement.naturalPowers };
    return undefined;
}

function validateConceptTargets(segmentId: string, arrangement: PrimitiveConceptArrangement, targets: readonly PrimitiveConceptTarget[]) {
    const kinds = conceptWheelValueKinds(arrangement);
    const wheel = conceptNaturalWheel(arrangement);
    const keys = new Set<string>();
    for (const target of targets) {
        if (target.targetKind === 'natural-assembly-band') {
            if (arrangement.arrangementKind !== 'natural-bands'
                || (arrangement.bandKind !== 'equivalence' && arrangement.bandKind !== target.bandKind)) {
                throw new Error(`Concept presentation '${segmentId}' targets an Assembly band absent from its arrangement.`);
            }
            keys.add(`assembly:${target.bandKind}`);
            continue;
        }
        if (target.targetKind === 'boolean-disc') {
            if (!kinds.includes('boolean')) {
                throw new Error(`Concept presentation '${segmentId}' targets a Boolean disc absent from its arrangement.`);
            }
            keys.add('boolean:disc');
            continue;
        }
        const valueKind = target.targetKind === 'primitive-wheel' ? target.valueKind : 'natural';
        if (!kinds.includes(valueKind)) {
            throw new Error(`Concept presentation '${segmentId}' targets a wheel absent from its arrangement.`);
        }
        if (target.targetKind !== 'primitive-wheel' && target.targetKind !== 'natural-angular-band') {
            if (!wheel?.powers.includes(target.power)) {
                throw new Error(`Concept presentation '${segmentId}' targets a Natural band absent from its arrangement.`);
            }
            if (target.targetKind === 'natural-cells' && target.cells === 'counted' && Math.floor(wheel.value / 10 ** target.power) % 10 === 0) {
                throw new Error(`Concept presentation '${segmentId}' targets counted cells of a band that counts none.`);
            }
        }
        const key = target.targetKind === 'natural-angular-band' ? `${valueKind}:angular:${target.digit}`
            : target.targetKind !== 'primitive-wheel' ? `${valueKind}:${target.power}` : valueKind;
        keys.add(key);
    }
    const wheelKeys = targets.filter(target => target.targetKind === 'primitive-wheel').map(target => target.valueKind);
    if (keys.size !== targets.length
        || (targets.some(target => target.targetKind === 'natural-angular-band')
            && targets.some(target => target.targetKind === 'natural-band' || target.targetKind === 'natural-cells'))
        || (wheelKeys.includes('natural') && targets.some(target => target.targetKind !== 'primitive-wheel' && target.targetKind !== 'boolean-disc'))
        || (wheelKeys.includes('boolean') && targets.some(target => target.targetKind === 'boolean-disc'))) {
        throw new Error(`Concept presentation '${segmentId}' emphasizes the same cells twice.`);
    }
}

export function conceptWheelValueKinds(arrangement: PrimitiveConceptArrangement): readonly ('natural' | 'boolean')[] {
    if (arrangement.arrangementKind === 'primitive-overview') return ['natural', 'boolean'];
    if (arrangement.arrangementKind === 'natural-wheel') return ['natural'];
    if (arrangement.arrangementKind === 'boolean-wheel') return ['boolean'];
    return [];
}

/** Camera reset identity; Assembly layouts navigate between views without resetting. */
export function primitiveConceptLayoutKey(arrangement: PrimitiveConceptArrangement): string {
    if (arrangement.arrangementKind === 'natural-wheel') return `natural-wheel:${arrangement.powers.join(',')}`;
    if (arrangement.arrangementKind === 'boolean-wheel') return 'boolean-wheel';
    if (arrangement.arrangementKind === 'primitive-overview') return `primitive-overview:${arrangement.naturalPowers.join(',')}`;
    return 'natural-bands';
}

/** Source documents contain no episode identity, narration, cue bindings or presentation policy. */
export function parsePrimitiveVisualSources(value: unknown): ReadonlyMap<string, PrimitiveVisualSource> {
    return parseEpisodeVisualSources(value, {
        parseTrace(source) {
            fields(source, ['trace', 'divisionComparison']);
            const trace = parseTraceReference(source.trace);
            if (trace.adapter !== 'primitive-space' || trace.traceKind !== 'PrimitiveTrace') {
                throw new Error('Primitive visual source requires a Primitive trace.');
            }
            if (source.divisionComparison !== undefined && source.divisionComparison !== 'explicit' && source.divisionComparison !== 'integrated') {
                throw new Error('Primitive divisionComparison must be explicit or integrated when supplied.');
            }
            return { trace,
                ...(source.divisionComparison === undefined ? {} : { divisionComparison: source.divisionComparison }) };
        },
        parseArrangement(value) {
            const arrangement = parseConceptArrangement(value);
            if (arrangement.arrangementKind === 'natural-wheel' && arrangement.bandLabels?.sweep) {
                throw new Error('Primitive visual source cannot contain a cue-bound band sweep.');
            }
            return arrangement;
        },
    });
}

export function parsePrimitiveEpisode(value: unknown, sources?: ReadonlyMap<string, PrimitiveVisualSource>): PrimitiveEpisode {
    const episode = record(value);
    fields(episode, ['handle', 'schemaVersion', 'title', 'scenes', 'cameraNavigationMode', 'comments']);
    if (episode.cameraNavigationMode !== undefined && episode.cameraNavigationMode !== 'guided') {
        throw new Error('Primitive episode cameraNavigationMode must be guided when supplied.');
    }
    if (episode.schemaVersion !== 2) throw new Error('Unsupported Primitive episode schema version.');
    const handle = text(episode.handle);
    const title = text(episode.title);
    if (!Array.isArray(episode.scenes) || !episode.scenes.length) throw new Error('Primitive episode requires scenes.');
    const ids = new Set<string>();
    const scenes: PrimitiveScene[] = episode.scenes.map((value) => {
        let scene = record(value);
        if (scene.visualSource !== undefined) {
            if (scene.sceneKind !== 'trace') throw new Error('Scene visualSource requires a trace scene; concepts select sources per beat.');
            scene = resolveEpisodeVisualSource(scene, sources, 'trace', ['trace', 'divisionComparison']);
        }
        const id = text(scene.id);
        if (ids.has(id)) throw new Error(`Duplicate scene '${id}'.`);
        ids.add(id);
        const sceneTitle = text(scene.title);
        if (scene.sceneKind === 'trace') {
            fields(scene, ['sceneKind', 'id', 'title', 'trace', 'beats', 'divisionComparison', 'fullComparisons', 'cameraPolicy', 'excerpt', 'illustrativeQuotientBands', 'direction', 'comments']);
            const fullComparisons = scene.fullComparisons;
            if (fullComparisons !== undefined && (typeof fullComparisons !== 'number'
                || !Number.isInteger(fullComparisons) || fullComparisons < 0)) {
                throw new Error('Primitive fullComparisons must be a non-negative integer.');
            }
            const illustrativeQuotientBands = scene.illustrativeQuotientBands;
            if (illustrativeQuotientBands !== undefined && (typeof illustrativeQuotientBands !== 'number'
                || !Number.isInteger(illustrativeQuotientBands) || illustrativeQuotientBands < 3 || illustrativeQuotientBands > 10)) {
                throw new Error('Primitive illustrativeQuotientBands must be an integer from 3 to 10.');
            }
            const excerpt = parseEpisodeExcerpt(scene.excerpt);
            if (scene.divisionComparison !== undefined && scene.divisionComparison !== 'explicit' && scene.divisionComparison !== 'integrated') {
                throw new Error('Primitive divisionComparison must be explicit or integrated when supplied.');
            }
            if (!Array.isArray(scene.beats)) throw new Error('Primitive trace scene requires beats.');
            const rates: (number | undefined)[] = [];
            const continuous: (readonly string[] | undefined)[] = [];
            const rateAfterCues: (string | undefined)[] = [];
            const rampSteps: (number | undefined)[] = [];
            const sourceBeats = scene.beats.map(value => {
                const { animationRate, animationRateAfterCue, animationRateRampSteps, continuousCues, ...beat } = record(value);
                if (animationRateAfterCue !== undefined && (typeof animationRateAfterCue !== 'string' || animationRate === undefined)) {
                    throw new Error('Primitive animationRateAfterCue must be a cue identifier on a beat with animationRate.');
                }
                rateAfterCues.push(animationRateAfterCue);
                if (animationRateRampSteps !== undefined && (!Number.isInteger(animationRateRampSteps)
                    || (animationRateRampSteps as number) < 1 || animationRateAfterCue === undefined)) {
                    throw new Error('Primitive animationRateRampSteps must be a positive integer on a beat with animationRateAfterCue.');
                }
                rampSteps.push(animationRateRampSteps as number | undefined);
                if (continuousCues !== undefined && (!Array.isArray(continuousCues)
                    || continuousCues.some(id => typeof id !== 'string') || new Set(continuousCues).size !== continuousCues.length)) {
                    throw new Error('Primitive continuousCues must be unique cue identifiers.');
                }
                continuous.push(continuousCues);
                if (animationRate !== undefined && (typeof animationRate !== 'number'
                    || !Number.isFinite(animationRate) || animationRate <= 0)) {
                    throw new Error('Primitive beat animationRate must be a finite positive number.');
                }
                rates.push(animationRate);
                return beat;
            });
            const plan = parseTraceScenePlan({ schemaVersion: 1, title: sceneTitle, trace: scene.trace, beats: sourceBeats }, parsePrimitivePlanEffects);
            plan.beats.forEach((beat, index) => {
                if (continuous[index]?.some(id => !beat.cues?.some(cue => cue.id === id))) {
                    throw new Error('Primitive continuousCues references an unknown cue.');
                }
                if (rateAfterCues[index] !== undefined && !beat.cues?.some(cue => cue.id === rateAfterCues[index])) {
                    throw new Error('Primitive animationRateAfterCue references an unknown cue.');
                }
            });
            if (plan.trace.adapter !== 'primitive-space') throw new Error('Primitive scene requires a Primitive trace.');
            const cameraPolicy = parseEpisodeTraceCameraPolicy(scene.cameraPolicy, plan.beats);
            return { sceneKind: 'trace', id, title: sceneTitle, trace: plan.trace,
                ...parseEpisodeComments(scene.comments),
                ...(scene.direction === undefined ? {} : { direction: parsePrimitiveSceneDirection(scene.direction) }),
                ...(illustrativeQuotientBands === undefined ? {} : { illustrativeQuotientBands }),
                ...(scene.divisionComparison === undefined ? {} : { divisionComparison: scene.divisionComparison }),
                ...(fullComparisons === undefined ? {} : { fullComparisons }),
                ...(cameraPolicy === undefined ? {} : { cameraPolicy }),
                ...(excerpt === undefined ? {} : { excerpt }),
                beats: plan.beats.map((beat, index) => ({
                    ...beat, ...(rates[index] === undefined ? {} : { animationRate: rates[index] }),
                    ...(rateAfterCues[index] === undefined ? {} : { animationRateAfterCue: rateAfterCues[index] }),
                    ...(rampSteps[index] === undefined ? {} : { animationRateRampSteps: rampSteps[index] }),
                    ...(continuous[index] === undefined ? {} : { continuousCues: continuous[index] }),
                })) };
        }
        if (scene.sceneKind !== 'concept') throw new Error('Unknown Primitive scene kind.');
        fields(scene, ['sceneKind', 'id', 'title', 'beats', 'comments']);
        if (!Array.isArray(scene.beats) || !scene.beats.length) throw new Error('Concept scene requires beats.');
        const beatIds = new Set<string>();
        const beats: PrimitiveConceptBeat[] = scene.beats.map((value) => {
            const beat = resolveEpisodeVisualSource(normalizeAuthoredBeat(value, 'concept'), sources, 'concept', ['arrangement']);
            fields(beat, ['id', 'arrangement', 'explanation', 'spokenText', 'narrationTextMap', 'narrationPauses', 'readingHoldMs', 'cues', 'presentation', 'comments']);
            const arrangement = parseConceptArrangement(beat.arrangement);
            const id = text(beat.id);
            if (beatIds.has(id)) throw new Error(`Duplicate concept beat '${id}'.`);
            beatIds.add(id);
            if (typeof beat.readingHoldMs !== 'number' || !Number.isFinite(beat.readingHoldMs) || beat.readingHoldMs < 0) {
                throw new Error('Invalid concept reading hold.');
            }
            const explanation = text(beat.explanation);
            const spokenText = beat.spokenText === undefined ? undefined : text(beat.spokenText);
            const cues = beat.cues === undefined ? undefined : parseConceptCues(beat.cues, spokenText ?? explanation);
            const presentation = beat.presentation === undefined ? undefined
                : parseConceptPresentation(Array.isArray(beat.presentation)
                    ? beat.presentation.map(segment => resolveEpisodeVisualSource(record(segment), sources, 'concept', ['arrangement']))
                    : beat.presentation, cues, parseConceptArrangement, parseConceptTarget,
                    { cameraView: true, orbit: true });
            if (arrangement.arrangementKind === 'natural-wheel' && arrangement.bandLabels?.sweep) {
                throw new Error('Band sweeps must start at an inline presentation cue, not at beat entry.');
            }
            let effective = arrangement;
            for (const segment of presentation ?? []) {
                effective = segment.arrangement ?? effective;
                if (segment.arrangement?.arrangementKind === 'natural-wheel' && segment.arrangement.bandLabels?.sweep
                    && segment.arrangement.bandLabels.sweep.afterCue !== segment.afterCue) {
                    throw new Error('Band sweep must reference its own presentation cue.');
                }
                validateConceptTargets(segment.id, effective, segment.effects
                    .filter(effect => effect.effectKind === 'emphasis').map(effect => effect.target));
                for (const effect of segment.effects) {
                    if (effect.effectKind === 'orbit') {
                        validateConceptTargets(segment.id, effective, [effect.target]);
                        continue;
                    }
                    if (effect.marks?.includes('contour') && ((effect.target.targetKind === 'natural-cells'
                        && effect.target.cells !== 'all') || (effect.target.targetKind === 'natural-assembly-band'
                        && effect.target.part !== 'band'))) {
                        throw new Error('Contour emphasis requires a whole wheel or band, not individual cells.');
                    }
                    if (effect.marks?.includes('border') && effect.target.targetKind === 'primitive-wheel') {
                        throw new Error('Border emphasis requires outlinable cells or bands; use contour for a whole wheel.');
                    }
                }
            }
            return { id, explanation, readingHoldMs: beat.readingHoldMs,
                ...parseEpisodeComments(beat.comments),
                ...(beat.narrationTextMap === undefined ? {} : {
                    narrationTextMap: parseNarrationTextMap(beat.narrationTextMap, text(beat.spokenText), explanation) }),
                ...(beat.narrationPauses === undefined ? {} : {
                    narrationPauses: parseNarrationPauses(beat.narrationPauses, spokenText ?? explanation) }),
                ...(spokenText === undefined ? {} : { spokenText }),
                ...(cues === undefined ? {} : { cues }),
                ...(presentation === undefined ? {} : { presentation }),
                arrangement };
        });
        return { sceneKind: 'concept', id, title: sceneTitle, beats, ...parseEpisodeComments(scene.comments) };
    });
    return { handle, title, schemaVersion: 2, scenes,
        ...parseEpisodeComments(episode.comments),
        ...(episode.cameraNavigationMode === 'guided' ? { cameraNavigationMode: 'guided' as const } : {}) };
}
